import React, { useState, useEffect } from 'react'
import { X, Check, Sparkles } from 'lucide-react'
import toast from 'react-hot-toast'
import { PLANS } from '../data/plansConfig'
import { startProCheckout } from '../services/billingService'
import { useUser } from '../contexts/UserContext'
import { isAdminUser } from '../utils/userProfileFields'
import { toastCustomerError } from '../utils/customerError'

const PremiumModal = ({ isOpen, onClose }) => {
  const { user } = useUser()
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      const onKeyDown = (e) => {
        if (e.key === 'Escape') onClose()
      }
      window.addEventListener('keydown', onKeyDown)
      return () => {
        document.body.style.overflow = ''
        window.removeEventListener('keydown', onKeyDown)
      }
    }
    document.body.style.overflow = ''
  }, [isOpen, onClose])
  const [isLoading, setIsLoading] = useState(false)
  const pro = PLANS.pro
  const free = PLANS.free

  const handleUpgrade = async () => {
    setIsLoading(true)
    try {
      await startProCheckout()
    } catch (error) {
      toastCustomerError(error, "We couldn't start checkout. Please try again.", {
        isAdmin: isAdminUser(user)
      })
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[99999]" onClick={onClose}>
      <div
        className="bg-ink-soft border border-ivory/10 text-ivory rounded-2xl shadow-2xl shadow-black/60 mx-4 max-h-[90vh] overflow-y-auto w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8">
          <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gold/20 border border-gold/30 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-gold" />
            </div>
            <h2 className="font-serif text-2xl font-medium text-ivory">
              Upgrade to Pro
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ivory-faint hover:text-ivory transition-colors"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <>
            <div className="mb-8">
              <div className="rounded-xl border border-gold/25 bg-gold/5 p-6">
                <div className="text-center">
                  <div className="font-serif text-5xl font-medium text-ivory mb-2">${pro.priceMonthlyUsd}</div>
                  <div className="text-ivory-muted">per month</div>
                  <div className="text-sm text-ivory-faint mt-1">Cancel anytime</div>
                </div>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <h3 className="text-lg font-semibold text-ivory mb-4">More generations every week</h3>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gold/15 rounded-full flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-gold" />
                  </div>
                  <span className="text-ivory-muted">{pro.weekly.pod} graphic + {pro.weekly.custom} Custom generations per week</span>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gold/15 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-gold" />
                  </div>
                  <span className="text-ivory-muted">
                    Free includes every feature with {free.weekly.pod} graphic + {free.weekly.custom} Custom per week
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-gold/15 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-gold" />
                  </div>
                  <span className="text-ivory-muted">Canvas edits never count against your limit</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <button
                onClick={handleUpgrade}
                disabled={isLoading}
                className="btn-primary w-full py-3 text-base"
              >
                {isLoading ? 'Redirecting to checkout…' : `Upgrade to Pro, $${pro.priceMonthlyUsd}/mo`}
              </button>
              
              <button
                onClick={onClose}
                className="btn-secondary w-full"
              >
                Maybe Later
              </button>
            </div>

            <div className="mt-6 pt-6 border-t border-ivory/10">
              <p className="text-xs text-ivory-faint text-center">
                Your subscription will automatically renew each month. You can cancel anytime in your account settings.
              </p>
            </div>
          </>
        </div>
      </div>
    </div>
  )
}

export default PremiumModal 